<template>
  <div class="modal-content responsive-mh-none responsive-pb-0">
    <!-- MODAL HEADER -->
    <div class="modal-header">
      <h1 class="modal-title mt-0 heading-large">Calculated Offer Price</h1>
      <button v-if="!$breakpoint.mdAndDown" type="button" class="modal-close" @click="$emit('close-modal', false)" aria-label="Cancel">
        <i class="fas fa-lg fa-times"></i>
      </button>
    <!-- MODAL HEADER -->
    </div>

    <!-- MODAL BODY -->
    <div class="modal-body pt-0">
      <!-- ROW -->
      <div v-show="offer.pricingMethod !== 'FIXED'" class="row mb-3">
        <!-- COLUMN -->
        <div class="col-md-12">
          <div class="bg-olf-isabelline p-3">
            <!-- SETTLE PRICE -->
            <div class="d-flex justify-content-between heading-medium">
              <span>Settle Base Price</span>
              <span>{{ dailySettlementOverride(productSelected && productSelected.totalFixed) | priceformatPPL }}</span>
            </div>

            <!-- MARKET CHANGE -->
            <div v-if="offer.pricingMethod === 'TRACKING'" class="d-flex justify-content-between heading-medium">
              <span>Market Change</span>
              <span>{{ dailySettlementOverride(productSelected && getMarketChange(productSelected)) | priceformatPPL }}</span>
            </div>

            <!-- LIVE BASE PRICE -->
            <div v-if="offer.pricingMethod === 'TRACKING'" class="d-flex justify-content-between heading-medium">
              <span>Live Base Price</span>
              <span>{{ dailySettlementOverride(productSelected && productSelected.totalLive) | priceformatPPL }}</span>
            </div>

            <!-- PREMIUM -->
            <div v-if="!isPricingEngineEnabled || offer.priceInputType === 'TOTAL'" class="d-flex justify-content-between heading-medium">
              <span>Premium</span>
              <span>{{ dailySettlementOverride(offer.premium) | priceformatPPL }}</span>
            </div>
            <!-- PRICING ENGINE COST BREAKDOWN -->
            <template v-else-if="offer.pricingMethod !== 'FIXED'">
              <!-- COST ADDITIONS -->
              <div v-if="rules.costAdditions" class="d-flex justify-content-between heading-medium">
                <span>{{ rules.costAdditions.title }}</span>
                <span>{{ dailySettlementOverride(prices.breakdown[rules.costAdditions.componentKey]) | priceformatPPL }}</span>
              </div>
              <!-- BIO RULES  -->
              <div v-if="rules.bioRules && isCumulativeRuleGroupRelevantForOffer(costAdditionScopeGetter, rules.bioRules.componentKey, offer, blendedProductConfig)" class="d-flex justify-content-between heading-medium">
                <span>{{ rules.bioRules.title }}</span>
                <span>{{ dailySettlementOverride(prices.breakdown[rules.bioRules.componentKey]) | priceformatPPL }}</span>
              </div>
              <!-- TEMPERATURE COST ADDITIONS -->
              <div v-if="rules.temperatureRules && isCumulativeRuleGroupRelevantForOffer(costAdditionScopeGetter, rules.temperatureRules.componentKey, offer, blendedProductConfig)" class="d-flex justify-content-between heading-medium">
                <span>{{ rules.temperatureRules.title }}</span>
                <span>{{ dailySettlementOverride(prices.breakdown[rules.temperatureRules.componentKey]) | priceformatPPL }}</span>
              </div>
            </template>
          </div>
        <!-- COLUMN -->
        </div>
      <!-- ROW -->
      </div>

      <!-- ROW -->
      <div class="row mb-3">
        <!-- COLUMN -->
        <div class="col-md-12">
          <div class="bg-olf-isabelline p-3">
            <!-- OFFER FINAL PRICE -->
            <div class="d-flex justify-content-between heading-xlarge font-weight-bold">
              <span>Offer Price</span>
              <span v-if="offer.pricingMethod === 'FIXED'">{{ prices.total | priceformatPPL }}</span>
              <span v-else>{{ dailySettlementOverride(prices.total) | priceformatPPL }}</span>
            </div>
          </div>
        <!-- COLUMN -->
        </div>
      <!-- ROW -->
      </div>

      <div v-if="!isSettlementSet || errors.length > 0" class="row mx-0 mb-3">
        <div 
          class="col-md-12 px-0"
          :class="{'no-results d-flex' : $breakpoint.mdAndDown}"
        >
          <div class="w-100 alert alert-danger my-0 text-center">
            <i class="fas fa-info-circle mr-2"></i>
            <!-- Flag telling seller that prices are not adjustable until settlement prices are updated -->
            <span v-if="!isSettlementSet">Settlement Prices must be updated before prices can be edited</span>
            <!-- Error messages -->
            <span v-else>{{ errors[0].message }}</span>
          </div>
        </div>
      </div>

      <!-- ROW -->
      <div class="row">
        <!-- COLUMN -->
        <div class="col-md-12 text-center">

          <!-- SAVE BUTTON -->
          <el-button
          class="btn-primary w-100 my-2"
          :loading="btnLoading"
          type="success"
          :disabled="!isFormComplete || !detailsChanged" @click="$emit('save-offer')"
          >
            <span v-if="isEditing">Save Changes</span>
            <span v-else>Create Offer </span>
          </el-button>
          <!-- FOOTER -->
          <div class="float-right my-2 d-flex flex-column">
            <img src="@/assets/images/rebrandLogo2.png" class="logo mt-2 ml-auto" :class="{'mw-200': !$breakpoint.smAndDown}" alt="OnlineFuels" />
          </div>
        <!-- COLUMN -->
        </div>
      <!-- ROW -->
      </div>
    <!-- MODAL BODY -->
    </div>
  </div>
</template>

<script>
import breakpoint from '@/mixins/index';
import { mapGetters } from "vuex";
import { marketChangePpl } from "@/util/calculations";
import { calculateCostAdditionPpl, isCumulativeRuleGroupRelevantForOffer } from "@/util/pricingRules";
import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";

export default {
  mixins: [breakpoint],
  props: {
    btnLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    offer: {
      type: Object,
      required: true
    },
    rules: {
      type: Object,
      default: {}
    },
    isEditing: {
      type: Boolean,
      required: true
    },
    isSettlementSet: {
      type: Boolean,
      required: true
    },
    isFormComplete: {
      type: Boolean,
      required: true
    },
    detailsChanged: {
      type: Boolean,
      default: true
    },
    errors: {
      type: Array,
      default: []
    }
  },
  data(){
    return {
      prices: {
        breakdown: {},
        total: 0
      },
      isCumulativeRuleGroupRelevantForOffer,
      costAdditionScopeGetter: costAdditionScope
    }
  },
  computed: {
    ...mapGetters('livePrices', ['getPrices']),
    ...mapGetters('login', ['isPricingEngineEnabled']),
    ...mapGetters('metadata', ['productConfig', 'blendedProductConfig']),
    ...mapGetters('rules', ['globalRulesGetter']),
    productSelected(){
      if (this.offer.fuel && this.getPrices)
        return this.getPrices.find(fuel => fuel.fuel_id == this.offer.fuel._id)
    },
    getPriceBreakdown: function(){
      if (!this.isPricingEngineEnabled || this.offer.priceInputType === 'TOTAL'){
        this.prices.total = this.offer.totalPrice;
      } else if (this.productSelected) { // Let's use pricing engine calculations to work out final offer price
        this.prices.total = this.offer.pricingMethod === 'TRACKING' ? this.productSelected.totalLive : this.productSelected.totalFixed;
        
        for (const ruleType in this.rules){
          // Remove rules that are to be excluded before any calculation happens
          const ruleTypeObject = JSON.parse(JSON.stringify(this.rules[ruleType]));
          const rulesForCalculation = ruleTypeObject && ruleTypeObject.rules.filter((rule, idx) => !rule.isRemoved && !rule.isOverridden);
          
          const costAddition = this.getTotalCostAdditions(rulesForCalculation);
          this.prices.breakdown[ruleTypeObject.componentKey] = costAddition; // Add the cost based on the cost addition category
          this.prices.total += costAddition; // Add the cost to the total price
        }
      }
    }
  },
  watch: {
    getPriceBreakdown(){}
  },
  methods: {
    dailySettlementOverride: function(value){
      if (!this.isSettlementSet)
        return 'N/A';
      return value;
    },
    getMarketChange: function(product){
      const settlePpl = product.totalFixed;
      const livePpl = product.totalLive;
      return marketChangePpl(settlePpl, livePpl);
    },
    getTotalCostAdditions: function(rules){ // Get total cost additions acccording to their category
      let totalCostAddition = 0;
      const fxSettlementRule = this.globalRulesGetter && this.globalRulesGetter.dailySettlementRules && this.globalRulesGetter.dailySettlementRules.find(rule => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.Fx);
      const fxSettle = fxSettlementRule ? Number(fxSettlementRule.action.value) : this.$store.getters["livePrices/marketData"].fx.settle;
      const iceLive = this.$store.getters["livePrices/marketData"].ice.live;
      const staMatrix = this.$store.getters["metadata/staMatrix"];
      const isBlendedProduct = this.blendedProductConfig.includes(this.offer.fuel._id);

      for (const rule of rules) {
        // sometimes cleaves sends it with a comma or multiple comma this will break the pricing calculation
        rule.action.value = Number((rule.action.value ?? 0).toString().replace(/,/g, '')).toFixed(2);
        const costAdditionPpl = calculateCostAdditionPpl(rule, this.productSelected, isBlendedProduct, fxSettle, iceLive, this.productConfig, staMatrix);
        totalCostAddition += costAdditionPpl;
      }

      return totalCostAddition; 
    }
  }
}
</script>

<style lang="scss" scoped>
.mw-200 {
  max-width: 200px;
}
</style>